import { useState, useEffect, useRef } from 'react';

interface AudioPlayerHook {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  play: (startTime: number, endTime: number) => Promise<void>;
  pause: () => void;
  cleanup: () => void;
}

export function useAudioPlayer(audioUrl: string | null): AudioPlayerHook {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMobile] = useState(() => 
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const endTimeRef = useRef<number | null>(null);
  const playAttemptRef = useRef<number>(0);
  const previousStartTimeRef = useRef<number>(0);
  const timeUpdateIntervalRef = useRef<number | null>(null);
  const segmentTimerRef = useRef<number | null>(null);
  const playbackTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!audioUrl) {
      return () => {};
    }

    try {
      // Clean up any existing audio element
      if (audioRef.current) {
        console.log('Cleaning up existing audio element');
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      // Handle both absolute and relative URLs
      const fullUrl = audioUrl.startsWith('http') || audioUrl.startsWith('//')
        ? audioUrl
        : audioUrl.startsWith('/')
          ? `${window.location.origin}${audioUrl}`
          : `${window.location.origin}/${audioUrl}`;

      // Use a DOM-created element with playsinline so iOS routes audio through
      // the speaker even when the audio session is in PlayAndRecord mode (mic active).
      const audio = document.createElement('audio');
      audio.setAttribute('playsinline', '');
      audio.setAttribute('webkit-playsinline', '');

      // Set up error handling before setting src
      audio.onerror = (e) => {
        // Skip error logging if src is empty (during cleanup)
        if (!audio.src) return;
        
        console.error('Audio loading error:', {
          url: fullUrl,
          error: e,
          code: audio.error?.code,
          message: audio.error?.message
        });
        if (mountedRef.current) {
          setIsPlaying(false);
        }
      };

      // Set up other event handlers
      audio.onloadedmetadata = () => {
        console.log(`🎼 Audio metadata loaded: duration=${audio.duration}s, sampleRate=${audio.mozSampleRate || 'N/A'}`);
        if (mountedRef.current) {
          setDuration(audio.duration);
        }
      };

      audio.ontimeupdate = () => {
        if (mountedRef.current) {
          setCurrentTime(audio.currentTime);
        }
        
        // Check if we've reached the end time of the segment
        if (endTimeRef.current && audio.currentTime >= endTimeRef.current) {
          audio.pause();
          if (mountedRef.current) {
            setIsPlaying(false);
          }
          endTimeRef.current = null;
          
          // Clear any interval
          if (timeUpdateIntervalRef.current) {
            window.clearInterval(timeUpdateIntervalRef.current);
            timeUpdateIntervalRef.current = null;
          }
        }
      };

      audio.onended = () => {
        if (mountedRef.current) {
          setIsPlaying(false);
        }
        
        // Clear any interval
        if (timeUpdateIntervalRef.current) {
          window.clearInterval(timeUpdateIntervalRef.current);
          timeUpdateIntervalRef.current = null;
        }
      };

      // Set the source last
      audio.src = fullUrl;
      audio.load();

      // Ensure playback rate is always 1.0 (normal speed)
      audio.playbackRate = 1.0;

      audioRef.current = audio;

      return () => {
        mountedRef.current = false;
        if (timeUpdateIntervalRef.current) {
          window.clearInterval(timeUpdateIntervalRef.current);
          timeUpdateIntervalRef.current = null;
        }
        
        if (segmentTimerRef.current) {
          window.clearTimeout(segmentTimerRef.current);
          segmentTimerRef.current = null;
        }
        
        audio.pause();
        audio.src = '';
        audioRef.current = null;
      };
    } catch (error) {
      console.error('Error initializing audio:', error);
      if (mountedRef.current) {
        setIsPlaying(false);
      }
      return () => {};
    }
  }, [audioUrl]);

  const play = async (startTime: number, endTime: number): Promise<void> => {
    console.log(`🎵 play() called with startTime: ${startTime.toFixed(3)}, endTime: ${endTime.toFixed(3)}`);

    if (!audioRef.current) return Promise.resolve();


      if (isMobile && audioRef.current) {
        // On mobile, always pause first and reset state before attempting to play a new segment
        console.log('Mobile platform detected, ensuring clean state before playback');
        audioRef.current.pause();
        if (mountedRef.current) {
          setIsPlaying(false);
        }
      }
    try {
      // Special case: if both start and end are 0, just pause the current playback
      if (startTime === 0 && endTime === 0) {
        pause();
        return;
      }

      // Clear any existing timers
      if (timeUpdateIntervalRef.current) {
        window.clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      
      if (segmentTimerRef.current) {
        window.clearTimeout(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }
      
      if (playbackTimerRef.current) {
        window.clearTimeout(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }

      if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
        console.error('Invalid time values:', { startTime, endTime });
        return;
      }

      // Validate times against the audio duration
      const audioDuration = audioRef.current.duration || 0;
      if (isNaN(audioDuration) || audioDuration <= 0) {
        console.warn('Audio duration not available yet, using provided values');
      } else {
        startTime = Math.max(0, Math.min(startTime, audioDuration));
        endTime = Math.min(Math.max(startTime, endTime), audioDuration);
      }

      // Set the end time reference for the segment
      endTimeRef.current = endTime;
      
      // Store the start time for comparison
      previousStartTimeRef.current = startTime;
      
      // Pause first to ensure clean state
      audioRef.current.pause();

      // Increment attempt counter to prevent race conditions
      playAttemptRef.current += 1;
      const currentAttempt = playAttemptRef.current;

      // Wait for seek to complete before playing
      return new Promise<void>((resolve, reject) => {
        if (!audioRef.current) {
          resolve();
          return;
        }

        const audio = audioRef.current;

        // Check if audio is ready for seeking
        console.log(`Audio ready state: ${audio.readyState}, Network state: ${audio.networkState}`);

        // Ensure we have enough data loaded (readyState >= 2 means HAVE_CURRENT_DATA)
        if (audio.readyState < 2) {
          console.warn('Audio not ready for seeking, waiting for loadeddata event');
          const onLoadedData = () => {
            audio.removeEventListener('loadeddata', onLoadedData);
            console.log('Audio data loaded, proceeding with seek');
            performSeekAndPlay();
          };
          audio.addEventListener('loadeddata', onLoadedData, { once: true });
          return;
        }

        performSeekAndPlay();

        function performSeekAndPlay() {
          if (!audioRef.current) {
            resolve();
            return;
          }

        // Set up one-time seeked event listener
        const onSeeked = async () => {
          audio.removeEventListener('seeked', onSeeked);

          if (currentAttempt !== playAttemptRef.current || !audioRef.current) {
            resolve();
            return;
          }

          // Add a small delay to ensure the seek has fully settled
          await new Promise(r => setTimeout(r, 50));

          // Verify we're at the correct position
          let actualTime = audioRef.current.currentTime;
          let timeDiff = Math.abs(actualTime - startTime);
          console.log(`Seeked complete. Requested: ${startTime.toFixed(3)}, Actual: ${actualTime.toFixed(3)}, Diff: ${timeDiff.toFixed(3)}s`);

          // If the difference is too large, force correct position
          let retries = 0;
          while (timeDiff > 0.05 && retries < 3) {
            console.warn(`Seek inaccurate (diff: ${timeDiff.toFixed(3)}s), retry ${retries + 1}/3`);
            try {
              audioRef.current.currentTime = startTime;
              await new Promise(r => setTimeout(r, 100));
              actualTime = audioRef.current.currentTime;
              timeDiff = Math.abs(actualTime - startTime);
              console.log(`After retry ${retries + 1}: ${actualTime.toFixed(3)}, Diff: ${timeDiff.toFixed(3)}s`);
              retries++;
            } catch (err) {
              console.error('Error correcting seek position:', err);
              break;
            }
          }

          if (timeDiff > 0.05) {
            console.error(`Failed to achieve accurate seek after ${retries} retries. Final diff: ${timeDiff.toFixed(3)}s`);
          }

          // Set up a time update interval that works consistently across platforms
          timeUpdateIntervalRef.current = window.setInterval(() => {
            if (!audioRef.current || !endTimeRef.current) {
              clearInterval(timeUpdateIntervalRef.current!);
              timeUpdateIntervalRef.current = null;
              return;
            }

            setCurrentTime(audioRef.current.currentTime);

            // Check if we've reached the end time
            if (audioRef.current.currentTime >= endTimeRef.current) {
              console.log(`End time reached: ${audioRef.current.currentTime} >= ${endTimeRef.current}`);
              audioRef.current.pause();

              if (mountedRef.current) {
                setIsPlaying(false);
              }

              if (timeUpdateIntervalRef.current) {
                clearInterval(timeUpdateIntervalRef.current);
                timeUpdateIntervalRef.current = null;
              }

              endTimeRef.current = null;
            }
          }, 50);  // Consistent update interval

          // Start playback with proper error handling
          console.log(`Starting playback at ${startTime}, will end at ${endTime}`);
          console.log(`Audio properties: playbackRate=${audioRef.current.playbackRate}, duration=${audioRef.current.duration}, currentTime=${audioRef.current.currentTime}`);

          const playPromise = audioRef.current.play();

          if (playPromise !== undefined) {
            playPromise.then(() => {
              if (mountedRef.current) {
                setIsPlaying(true);
              }

              // CRITICAL FIX: Verify position after play starts and correct if needed
              const positionChecks = [50, 100, 200];
              positionChecks.forEach((delay) => {
                setTimeout(() => {
                  if (!audioRef.current || !mountedRef.current) return;

                  const actualPosition = audioRef.current.currentTime;
                  const positionDiff = Math.abs(actualPosition - startTime);
                  const expectedPosition = startTime + (delay / 1000);

                  console.log(`Position check at ${delay}ms: Expected ~${expectedPosition.toFixed(3)}, Got ${actualPosition.toFixed(3)}, Diff from start: ${positionDiff.toFixed(3)}`);

                  // If position is significantly wrong (more than 1 second off), force correct it
                  if (positionDiff > 1.0) {
                    console.error(`🚨 MAJOR position drift detected! Expected near ${startTime.toFixed(3)}, Got ${actualPosition.toFixed(3)}`);
                    console.error(`   This suggests the audio is playing from wrong position. Forcing correction...`);
                    audioRef.current.pause();
                    audioRef.current.currentTime = startTime;
                    audioRef.current.play();
                  }
                }, delay);
              });

              // Set a backup timer to stop at end time
              const segmentDuration = endTime - startTime;
              segmentTimerRef.current = window.setTimeout(() => {
                console.log(`Segment backup timer reached after ${segmentDuration}s`);
                if (!audioRef.current) return;

                // Check if we're still playing this segment
                if (endTimeRef.current === endTime) {
                  audioRef.current.pause();
                  if (mountedRef.current) {
                    setIsPlaying(false);
                  }

                  if (timeUpdateIntervalRef.current) {
                    clearInterval(timeUpdateIntervalRef.current);
                    timeUpdateIntervalRef.current = null;
                  }

                  endTimeRef.current = null;
                }

                segmentTimerRef.current = null;
              }, (segmentDuration * 1000) + 200);

              resolve();
            }).catch(error => {
              console.error('Error playing audio:', error);
              if (mountedRef.current) {
                setIsPlaying(false);
              }

              if (timeUpdateIntervalRef.current) {
                clearInterval(timeUpdateIntervalRef.current);
                timeUpdateIntervalRef.current = null;
              }

              if (segmentTimerRef.current) {
                clearTimeout(segmentTimerRef.current);
                segmentTimerRef.current = null;
              }

              endTimeRef.current = null;
              reject(error);
            });
          } else {
            resolve();
          }
        };

        // Set up timeout fallback in case seeked event doesn't fire
        const seekTimeout = setTimeout(() => {
          audio.removeEventListener('seeked', onSeeked);
          console.warn('Seek timeout - proceeding anyway');
          onSeeked();
        }, 500);

        // Add the seeked event listener
        audio.addEventListener('seeked', () => {
          clearTimeout(seekTimeout);
          onSeeked();
        }, { once: true });

          // Set the current time - this will trigger the seeked event
          try {
            audio.currentTime = startTime;
          } catch (err) {
            clearTimeout(seekTimeout);
            audio.removeEventListener('seeked', onSeeked);
            console.error('Error setting currentTime:', err);
            reject(err);
          }
        }
      });
    } catch (error) {
      console.error('Error during audio playback:', error);
      if (mountedRef.current) {
        setIsPlaying(false);
      }

      // Clean up any timers
      if (timeUpdateIntervalRef.current) {
        window.clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }

      if (segmentTimerRef.current) {
        window.clearTimeout(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }

      return Promise.reject(error);
    }
  };

  const pause = () => {
    try {
      if (!audioRef.current) return;

      // Clear all timers first
      if (playbackTimerRef.current) {
        clearTimeout(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      
      if (segmentTimerRef.current) {
        clearTimeout(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }

      // Pause audio and update state
      audioRef.current.pause();
      
      // For mobile, explicitly reset to beginning if needed
      if (isMobile) {
        try {
          audioRef.current.currentTime = 0;
        } catch (err) {
          // Ignore errors on currentTime reset
        }
      }
      
      if (mountedRef.current) {
        setIsPlaying(false);
      }
      
      playAttemptRef.current = 0;
      endTimeRef.current = null;
    } catch (error) {
      console.error('Error pausing audio:', error);
      if (mountedRef.current) {
        setIsPlaying(false);
      }
    }
  };

  const cleanup = () => {
    try {
      if (timeUpdateIntervalRef.current) {
        if (playbackTimerRef.current) {
          clearTimeout(playbackTimerRef.current);
          playbackTimerRef.current = null;
        }
      
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      
      if (segmentTimerRef.current) {
        clearTimeout(segmentTimerRef.current);
        segmentTimerRef.current = null;
      }
      
      if (playbackTimerRef.current) {
        clearTimeout(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  };

  return {
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    cleanup
  };
}